'use client';

import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/common/dialog/Dialog';
import { Button } from '@/components/common/Button';

interface ImageInsertionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (url: string, alt: string, size: 'small' | 'medium' | 'large' | 'custom', customWidth?: number, customHeight?: number) => void;
  initialData?: {
    url: string;
    alt: string;
    size: 'small' | 'medium' | 'large' | 'custom';
    width?: number;
    height?: number;
  } | null;
  mode?: 'insert' | 'edit';
}

export const ImageInsertionDialog: React.FC<ImageInsertionDialogProps> = ({
  isOpen,
  onClose,
  onInsert,
  initialData,
  mode = 'insert'
}) => {
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');
  const [size, setSize] = useState<'small' | 'medium' | 'large' | 'custom'>('medium');
  const [customWidth, setCustomWidth] = useState<number | undefined>();
  const [customHeight, setCustomHeight] = useState<number | undefined>();
  const [isValidUrl, setIsValidUrl] = useState(true);

  // Initialize form with existing data when editing
  useEffect(() => {
    if (initialData) {
      setUrl(initialData.url);
      setAlt(initialData.alt);
      setSize(initialData.size);
      setCustomWidth(initialData.width);
      setCustomHeight(initialData.height);
      setIsValidUrl(true);
    }
  }, [initialData]);

  const validateUrl = (urlString: string) => {
    try {
      new URL(urlString);
      return true;
    } catch {
      return false;
    }
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
    if (value.trim()) {
      setIsValidUrl(validateUrl(value));
    } else {
      setIsValidUrl(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('DIALOG: Form submitted');
    console.log('DIALOG: Form data:', { url, alt, size, mode });
    
    if (!url.trim()) {
      setIsValidUrl(false);
      return;
    }

    if (!validateUrl(url)) {
      setIsValidUrl(false);
      return;
    }

    console.log('DIALOG: Calling onInsert with:', { url, alt: alt || 'Image', size, customWidth, customHeight });
    onInsert(url, alt || 'Image', size, customWidth, customHeight);
    handleClose();
    console.log('DIALOG: Dialog closed');
  };

  const handleClose = () => {
    setUrl('');
    setAlt('');
    setSize('medium');
    setCustomWidth(undefined);
    setCustomHeight(undefined);
    setIsValidUrl(true);
    onClose();
  };

  const sizeOptions = [
    { value: 'small', label: 'Small (300px)' },
    { value: 'medium', label: 'Medium (600px)' },
    { value: 'large', label: 'Large (900px)' },
    { value: 'custom', label: 'Custom' },
  ] as const;

  return (
    <Dialog open={isOpen} onOpenChange={() => handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit Image' : 'Insert Image'}
          </DialogTitle>
        </DialogHeader>
        
        <form id="image-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* URL Input */}
            <div>
              <label htmlFor="image-url" className="block text-sm font-medium text-gray-700 mb-1">
                Image URL *
              </label>
              <input
                type="text"
                id="image-url"
                value={url}
                onChange={(e) => handleUrlChange(e.target.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-stone-950 focus:ring-offset-2 sm:text-sm text-gray-900 ${
                  !isValidUrl 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-stone-500'
                }`}
                placeholder="https://example.com/image.jpg"
                autoFocus
              />
              {!isValidUrl && (
                <p className="mt-1 text-sm text-red-600">Please enter a valid URL</p>
              )}
            </div>

            {/* Alt Text Input */}
            <div>
              <label htmlFor="image-alt" className="block text-sm font-medium text-gray-700 mb-1">
                Alt Text
              </label>
              <input
                type="text"
                id="image-alt"
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-stone-950 focus:ring-offset-2 focus:border-stone-500 sm:text-sm text-gray-900"
                placeholder="Describe the image"
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional but recommended for accessibility
              </p>
            </div>

            {/* Size Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Size
              </label>
              <div className="grid grid-cols-2 gap-3">
                {sizeOptions.map((option) => {
                  const isCustomOption = option.value === 'custom';
                  const isCustomDisabled = isCustomOption && initialData?.size !== 'custom';
                  
                  return (
                    <div 
                      key={option.value} 
                      className={`flex items-center cursor-pointer hover:bg-gray-50 p-3 rounded border ${
                        size === option.value ? 'border-stone-500 bg-stone-50' : 'border-gray-200'
                      } ${isCustomDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => !isCustomDisabled && setSize(option.value)}
                    >
                      <input
                        type="radio"
                        name="size"
                        value={option.value}
                        checked={size === option.value}
                        disabled={isCustomDisabled}
                        onChange={(e) => setSize(e.target.value as 'small' | 'medium' | 'large' | 'custom')}
                        className="h-4 w-4 text-stone-600 border-gray-300 focus:ring-stone-500 pointer-events-none"
                      />
                      <div className="ml-3 flex-1">
                        <span className="text-sm font-medium text-gray-700 select-none pointer-events-none">
                          {option.label}
                        </span>
                        {isCustomOption && customWidth && customHeight && (
                          <div className="text-xs text-gray-500 mt-1">
                            {customWidth} × {customHeight}px
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {size && (
                <p className="mt-3 text-xs text-gray-500">
                  {size === 'small' && 'Good for icons and small illustrations'}
                  {size === 'medium' && 'Good for most images'}
                  {size === 'large' && 'Good for detailed images and screenshots'}
                  {size === 'custom' && 'Custom size - resize by dragging the image border in the editor'}
                </p>
              )}
            </div>
          </div>
        </form>

        <DialogFooter className="flex justify-end space-x-2">
          <Button onClick={handleClose} variant="outline" className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button 
            type="submit"
            form="image-form"
            disabled={!url.trim() || !isValidUrl}
            className="w-full sm:w-auto"
          >
            {mode === 'edit' ? 'Update Image' : 'Insert Image'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
